import { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const category = slug ? getCategoryBySlug(slug) : undefined;
  const articles = slug ? getArticlesByCategory(slug) : [];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [location.pathname]);

  if (!category) {
    return <NotFound />;
  }

  return (
    <>
      <SEO
        title={`${category.name} - 深度解析善意社交智慧`}
        description={category.description}
        keywords={`${category.name},人善交,结交善人,高质量人际关系,交友智慧`}
        canonicalUrl={`/category/${slug}`}
      />

      <Navbar />
      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="py-4">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: category.name },
              ]}
            />
          </div>

          <div className="py-8 md:py-12">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                <i className={`${category.icon} text-lg`}></i>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900"
                style={{ fontFamily: 'var(--font-heading)' }}>
                {category.name}
              </h1>
            </div>
            <p className="text-sm text-foreground-600 ml-13 mb-8">
              {category.description}
            </p>

            <div className="flex items-center gap-2 flex-wrap mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    cat.slug === slug
                      ? 'bg-accent-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                  }`}
                >
                  {cat.name}
                  <span className="ml-1 opacity-70">({cat.articleCount})</span>
                </Link>
              ))}
            </div>

            {articles.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-foreground-500">暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 border border-background-200/60 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:border-accent-300/50"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        title={article.title}
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full whitespace-nowrap">
                          {article.tags[0]}
                        </span>
                        <span className="text-xs text-foreground-500">
                          {article.readTime} 分钟阅读
                        </span>
                      </div>
                      <h3 className="text-base md:text-lg font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-accent-600 transition-colors"
                        style={{ fontFamily: 'var(--font-heading)' }}>
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-background-200/60">
                        <span className="text-xs font-medium text-foreground-700">{article.author}</span>
                        <span className="text-xs text-foreground-400">·</span>
                        <span className="text-xs text-foreground-500">{article.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}